import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Flag,
  RefreshCw,
  Ban,
  Globe,
  Mail,
  ArrowLeft,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PlutoUser from './PlutoUser';

const PlutoSingleWithdrawal = () => {
  const [data, setData] = useState({});
  const [showUser, setShowUser] = useState(false); // Start collapsed on mobile
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [giftCardData, setGiftCardData] = useState({ code: '', pin: '' });
  const { referral_id, page, second_page, third_page } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    socket.emit('getWithdrawAdmin', {
      withdraw_id: third_page
    });

    socket.on('response_get_admin_withdraw', (data) => {
      setData(data);
      setLoading(false);
    });

    socket.on('reTakeWithdrawDet', () => {
      socket.emit('getWithdrawAdmin', {
        withdraw_id: third_page
      });
    });

    return () => {
      socket.off('response_get_admin_withdraw');
      socket.off('reTakeWithdrawDet');
    };
  }, [third_page]);

  const getStatusInfo = (withdraw) => {
    if (withdraw.paid === 1) {
      return { status: 'Paid', bg: 'rgba(63,185,80,0.15)', color: '#3fb950', icon: CheckCircle };
    }
    if (withdraw.flagged === 1) {
      return { status: 'Flagged', bg: 'rgba(248,81,73,0.15)', color: '#f85149', icon: Flag };
    }
    if (withdraw.review === 1) {
      return { status: 'Review', bg: 'rgba(249,115,22,0.15)', color: '#f97316', icon: Eye };
    }
    return { status: 'Processing', bg: 'rgba(88,166,255,0.15)', color: '#58a6ff', icon: Clock };
  };

  const handleAction = (action) => {
    if (action === 'pay' && data.withdraw.type !== 'Crypto') {
      setShowActionModal(true);
    } else {
      executeAction(action);
    }
  };

  const executeAction = (action, additionalData = {}) => {
    const actions = {
      flag: () => socket.emit('adminAction', { action: 'flag', withdraw: data.withdraw.id }),
      review: () => socket.emit('adminAction', { action: 'review', withdraw: data.withdraw.id }),
      refund: () => socket.emit('adminAction', { action: 'refund_withdraw', withdraw: data.withdraw.id }),
      pay: () => socket.emit('adminAction', {
        action: 'pay_gift_card',
        withdraw: data.withdraw.id,
        ...additionalData
      })
    };

    actions[action]?.();
    setShowActionModal(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e6edf3', padding: '12px' }}>
        <div style={{ maxWidth: '512px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div className="animate-pulse">
            <div style={{ height: '16px', background: '#21262d', borderRadius: '6px', width: '128px', marginBottom: '8px' }}></div>
            <div style={{ height: '128px', background: '#161b22', borderRadius: '12px', marginBottom: '12px' }}></div>
            <div style={{ height: '96px', background: '#161b22', borderRadius: '12px' }}></div>
          </div>
        </div>
      </div>
    );
  }

  const { withdraw, user } = data;
  const statusInfo = getStatusInfo(withdraw);
  const StatusIcon = statusInfo.icon;

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e6edf3' }}>
      <div>
        {/* Sticky Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'rgba(13,17,23,0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #21262d',
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 10
        }}>
          <Link
            to={'/pluto/withdrawals'}
            style={{
              padding: '4px',
              borderRadius: '6px',
              transition: 'background 0.2s',
              color: '#e6edf3',
              textDecoration: 'none',
              display: 'inline-flex'
            }}
          >
            <ArrowLeft style={{ height: '20px', width: '20px' }} />
          </Link>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#e6edf3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Withdrawal #{withdraw.id}
            </div>
            <div style={{ fontSize: '12px', color: '#8b949e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.username} &bull; {withdraw.type}
            </div>
          </div>
          <div style={{
            padding: '4px 8px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 500,
            background: statusInfo.bg,
            color: statusInfo.color,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <StatusIcon style={{ height: '12px', width: '12px' }} />
            {statusInfo.status}
          </div>
        </div>

        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Amount Card - Ultra Compact */}
          <div style={{
            background: '#161b22',
            borderRadius: '12px',
            padding: '12px',
            border: '1px solid #21262d',
            borderLeft: '3px solid #3fb950'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#e6edf3' }}>
                  ${parseFloat(parseInt(withdraw.amount) / 1000).toFixed(3)}
                </div>
                <div style={{ fontSize: '12px', color: '#8b949e' }}>Requested</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#c9d1d9' }}>
                  Fee: ${parseFloat(parseInt(withdraw.details?.fee || 0) / 1000).toFixed(3)}
                </div>
                <div style={{ fontSize: '12px', color: '#8b949e' }}>
                  {withdraw.paid === 1 ? '\u2705 Paid' : '\u23F3 Pending'}
                </div>
              </div>
            </div>

            {/* Payment Method - Single Line */}
            <div style={{ fontSize: '14px', borderTop: '1px solid #21262d', paddingTop: '8px' }}>
              <span style={{ color: '#8b949e' }}>Method: </span>
              <span style={{ color: '#e6edf3' }}>{withdraw.option_request}</span>
            </div>

            {/* Collapsible Details */}
              <div style={{ marginTop: '12px', borderTop: '1px solid #21262d', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                {withdraw.type === "Crypto" ? (
                  <>
                    <div>
                      <span style={{ color: '#8b949e' }}>To: </span>
                      <span style={{ color: '#e6edf3', fontSize: '12px', wordBreak: 'break-all' }}>{withdraw.withdraw_to}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span style={{ color: '#8b949e' }}>Code: </span>
                      <span style={{ color: '#e6edf3' }}>{withdraw.details?.code || 'N/A'}</span>
                    </div>
                    <div>
                      <span style={{ color: '#8b949e' }}>Pin: </span>
                      <span style={{ color: '#e6edf3' }}>{withdraw.details?.pin || 'N/A'}</span>
                    </div>
                    <div>
                      <span style={{ color: '#8b949e' }}>Claimed: </span>
                      <span style={{ color: '#e6edf3' }}>{withdraw.details?.claimed || 'No'}</span>
                    </div>
                  </>
                )}
              </div>

            {/* Flag Reason */}
            {withdraw.flagged === 1 && withdraw.flag_reason && (
              <div style={{
                marginTop: '8px',
                padding: '8px',
                background: 'rgba(248,81,73,0.1)',
                border: '1px solid rgba(248,81,73,0.2)',
                borderRadius: '8px',
                fontSize: '12px'
              }}>
                <span style={{ color: '#f85149', fontWeight: 500 }}>Flag: </span>
                <span style={{ color: '#ffa198' }}>{withdraw.flag_reason}</span>
              </div>
            )}
          </div>

          {/* Action Buttons - Mobile Optimized */}
          {withdraw.paid !== 1 && !user.banned && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {withdraw.review !== 1 && (
                <>
                  {withdraw.type === "Crypto" ? (
                    <button
                      onClick={() => handleAction('flag')}
                      style={{
                        padding: '8px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        border: withdraw.flagged === 1 ? '1px solid #30363d' : '1px solid #d29922',
                        background: withdraw.flagged === 1 ? '#21262d' : 'rgba(210,153,34,0.15)',
                        color: withdraw.flagged === 1 ? '#c9d1d9' : '#d29922'
                      }}
                    >
                      <Flag style={{ height: '16px', width: '16px' }} />
                      {withdraw.flagged === 1 ? 'Unflag' : 'Flag'}
                    </button>
                  ) : (
                    withdraw.flagged === 1 && (
                      <button
                        onClick={() => handleAction('pay')}
                        style={{
                          padding: '8px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: 500,
                          background: '#238636',
                          border: '1px solid #2ea043',
                          color: '#ffffff',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        <CheckCircle style={{ height: '16px', width: '16px' }} />
                        Pay
                      </button>
                    )
                  )}
                </>
              )}

              <button
                onClick={() => handleAction('review')}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  background: 'rgba(88,166,255,0.15)',
                  border: '1px solid #58a6ff',
                  color: '#58a6ff',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}
              >
                <Eye style={{ height: '16px', width: '16px' }} />
                {withdraw.review === 1 ? 'Release' : 'Review'}
              </button>

              <button
                onClick={() => handleAction('refund')}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  background: 'rgba(248,81,73,0.15)',
                  border: '1px solid #f85149',
                  color: '#f85149',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}
              >
                <RefreshCw style={{ height: '16px', width: '16px' }} />
                Refund
              </button>
            </div>
          )}

          {/* User Quick Info */}
          <div style={{
            background: '#161b22',
            borderRadius: '12px',
            padding: '12px',
            border: '1px solid #21262d',
            borderLeft: '3px solid #58a6ff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ padding: '6px', background: 'rgba(88,166,255,0.15)', borderRadius: '6px' }}>
                  <User style={{ height: '16px', width: '16px', color: '#58a6ff' }} />
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#e6edf3' }}>{user.username}</div>
                  <div style={{ fontSize: '12px', color: '#8b949e' }}>ID: {user.id} &bull; {user.country}</div>
                </div>
              </div>
            </div>

            {/* Expandable User Details */}
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #21262d', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <div>
                    <span style={{ color: '#8b949e' }}>Email: </span>
                    <span style={{ color: '#e6edf3', wordBreak: 'break-all' }}>{user.emailProvider}</span>
                  </div>
                  <div>
                    <span style={{ color: '#8b949e' }}>Phone: </span>
                    <span style={{ color: '#e6edf3' }}>{user.phoneCountry || 'N/A'}</span>
                  </div>
                  <div>
                    <span style={{ color: '#8b949e' }}>Referee: </span>
                    <span style={{ color: '#e6edf3' }}>{user.referee || 'None'}</span>
                  </div>
                  <div>
                    <span style={{ color: '#8b949e' }}>Status: </span>
                    <span style={{ color: user.banned ? '#f85149' : '#3fb950' }}>
                      {user.banned ? 'Banned' : 'Active'}
                    </span>
                  </div>
                </div>

                {user.countries && (
                  <div style={{ fontSize: '12px' }}>
                    <span style={{ color: '#8b949e' }}>Countries: </span>
                    <span style={{ color: '#e6edf3' }}>{user.countries}</span>
                  </div>
                )}

                {/* User Component */}
                <div style={{ paddingTop: '8px', borderTop: '1px solid #21262d' }}>
                  <PlutoUser second_page={user.id} x={1} />
                </div>
              </div>
          </div>
        </div>

        {/* Gift Card Modal - Mobile Optimized */}
        {showActionModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            padding: 0,
            zIndex: 50
          }} className="sm:items-center sm:p-4">
            <div style={{
              background: '#161b22',
              borderRadius: '12px 12px 0 0',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              border: '1px solid #30363d'
            }} className="sm:rounded-lg sm:w-full sm:max-w-md">
              <div style={{ padding: '16px', borderBottom: '1px solid #21262d' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#e6edf3', margin: 0 }}>
                  Gift Card Details (#{withdraw.id})
                </h3>
              </div>

              <div style={{ padding: '16px' }}>
                {withdraw.option_request?.toLowerCase().includes("paypal") ? (
                  <p style={{ color: '#c9d1d9', marginBottom: '16px' }}>PayPal payment will be processed automatically.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', color: '#8b949e', marginBottom: '4px' }}>Gift Card Code</label>
                      <input
                        type="text"
                        placeholder="Enter gift card code"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          background: '#0d1117',
                          border: '1px solid #30363d',
                          borderRadius: '8px',
                          color: '#e6edf3',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#58a6ff'}
                        onBlur={(e) => e.target.style.borderColor = '#30363d'}
                        onChange={(e) => setGiftCardData({...giftCardData, code: e.target.value})}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', color: '#8b949e', marginBottom: '4px' }}>Gift Card Pin</label>
                      <input
                        type="text"
                        placeholder="Enter gift card pin"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          background: '#0d1117',
                          border: '1px solid #30363d',
                          borderRadius: '8px',
                          color: '#e6edf3',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#58a6ff'}
                        onBlur={(e) => e.target.style.borderColor = '#30363d'}
                        onChange={(e) => setGiftCardData({...giftCardData, pin: e.target.value})}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <button
                    onClick={() => setShowActionModal(false)}
                    style={{
                      padding: '8px 16px',
                      background: '#21262d',
                      border: '1px solid #30363d',
                      color: '#c9d1d9',
                      borderRadius: '8px',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => executeAction('pay', giftCardData)}
                    disabled={!withdraw.option_request?.toLowerCase().includes("paypal") && giftCardData.code.length < 5}
                    style={{
                      padding: '8px 16px',
                      background: '#238636',
                      border: '1px solid #2ea043',
                      color: '#ffffff',
                      borderRadius: '8px',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      opacity: (!withdraw.option_request?.toLowerCase().includes("paypal") && giftCardData.code.length < 5) ? 0.5 : 1
                    }}
                  >
                    Proceed
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlutoSingleWithdrawal;
